import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThreeDots } from 'react-loader-spinner';
import DefaultLayout from '../../reusableComponents/defaultLayout';
import AccountHeader from '../../components/account/AccountHeader';
import SummaryCard from '../../components/dashboard/SummaryCard';
import ProjectsAttention from '../../components/dashboard/ProjectsAttention';
import ActiveProjectsOverview from '../../components/dashboard/ActiveProjectsOverview';
import ActivityFeed from '../../components/dashboard/ActivityFeed';
import { API_BASE_URL } from '../../config/api';

// Icons
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GavelIcon from '@mui/icons-material/Gavel';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ContactsIcon from '@mui/icons-material/Contacts';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

export default function GCDashboard() {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        
        try {
            const response = await fetch(`${API_BASE_URL}/dashboard_summary`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setDashboardData(data.data);
                } else {
                    setError('Failed to load dashboard data');
                }
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err) {
            setError('An error occurred while loading dashboard data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <DefaultLayout>
                <AccountHeader activeTab="dashboard" />
                <section className="dashboard_main_content">
                    <div className="container">
                        <div className="dashboard_loading">
                            <ThreeDots
                                height="60"
                                width="80"
                                radius="9"
                                color="#B1903F"
                                ariaLabel="three-dots-loading"
                                visible={true}
                            />
                            <p>Loading dashboard...</p>
                        </div>
                    </div>
                </section>
            </DefaultLayout>
        );
    }

    const summary = dashboardData?.summary || {};

    return (
        <DefaultLayout>
            <AccountHeader activeTab="dashboard" />

            {/* Summary Cards - Row 1: As Project Owner */}
            <section className="dashboard_main_content">
                <div className="container">
                    {/* As Project Owner Section */}
                    <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '14px', color: '#707070', fontWeight: '500', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            As Project Owner
                        </h4>
                    </div>
                    <div className="dashboard_summary_cards">
                        <SummaryCard
                            title="My Projects Out for Bid"
                            value={summary.projects_out_for_bid || 0}
                            icon={WorkOutlineIcon}
                            subtitle={summary.new_bids_count > 0 ? `${summary.new_bids_count} new bids this week` : null}
                            onClick={() => navigate('/account/1')}
                        />
                        <SummaryCard
                            title="My Active Projects"
                            value={summary.active_projects || 0}
                            icon={TrendingUpIcon}
                            subtitle={summary.avg_completion > 0 ? `${summary.avg_completion}% avg completion` : null}
                            onClick={() => navigate('/account/2')}
                        />
                        <SummaryCard
                            title="Bids Received"
                            value={summary.total_bids_received || 0}
                            icon={GavelIcon}
                            trend={summary.new_bids_count > 0 ? 'up' : null}
                            trendValue={summary.new_bids_count > 0 ? `+${summary.new_bids_count}` : null}
                        />
                        <SummaryCard
                            title="My Contacts"
                            value={summary.contacts_count || 0}
                            icon={ContactsIcon}
                            onClick={() => navigate('/account/4')}
                        />
                    </div>

                    {/* As Contractor Section */}
                    <div style={{ marginTop: '40px', marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '14px', color: '#707070', fontWeight: '500', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            As Contractor
                        </h4>
                    </div>
                    <div className="dashboard_summary_cards" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                        <SummaryCard
                            title="Pending Bids Submitted"
                            value={summary.pending_bids_as_contractor || 0}
                            icon={HourglassEmptyIcon}
                            subtitle="Awaiting decision"
                            onClick={() => navigate('/find-a-project')}
                        />
                        <SummaryCard
                            title="Projects Won"
                            value={summary.projects_as_contractor || 0}
                            icon={AssignmentTurnedInIcon}
                            subtitle="Active contracts"
                        />
                    </div>

                    {/* Main Dashboard Grid - As Project Owner */}
                    <div style={{ marginTop: '40px', marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '14px', color: '#707070', fontWeight: '500', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Managing My Projects
                        </h4>
                    </div>
                    <div className="dashboard_grid">
                        <ProjectsAttention projects={dashboardData?.projects_attention} />
                        <ActiveProjectsOverview 
                            projects={dashboardData?.active_projects_overview} 
                            title="My Active Projects (with Subs)"
                        />
                    </div>

                    {/* Projects As Contractor */}
                    {dashboardData?.projects_as_contractor && dashboardData.projects_as_contractor.length > 0 && (
                        <>
                            <div style={{ marginTop: '40px', marginBottom: '16px' }}>
                                <h4 style={{ fontSize: '14px', color: '#707070', fontWeight: '500', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Projects I'm Working On
                                </h4>
                            </div>
                            <div className="dashboard_grid" style={{ gridTemplateColumns: '1fr' }}>
                                <ActiveProjectsOverview 
                                    projects={dashboardData.projects_as_contractor} 
                                    title="Active Contracts"
                                />
                            </div>
                        </>
                    )}

                    {/* Activity Feed and Quick Actions */}
                    <div className="dashboard_grid" style={{ marginTop: '24px' }}>
                        <ActivityFeed activities={dashboardData?.recent_activity} />
                        
                        {/* Quick Actions */}
                        <div className="dashboard_section">
                            <div className="section_header">
                                <h3>Quick Actions</h3>
                            </div>
                            <div className="dashboard_quick_actions" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0, flexDirection: 'column' }}>
                                <button 
                                    className="quick_action_btn primary"
                                    onClick={() => navigate('/post-new-project')}
                                >
                                    <AddIcon /> Post Project (for Subs)
                                </button>
                                <button 
                                    className="quick_action_btn"
                                    onClick={() => navigate('/find-a-project')}
                                >
                                    <SearchIcon /> Find Projects to Bid On
                                </button>
                                <button 
                                    className="quick_action_btn"
                                    onClick={() => navigate('/account/4')}
                                >
                                    <ContactsIcon /> Manage Contacts
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </DefaultLayout>
    );
}
